import { useCallback } from 'react';
import { formatISO9075 } from 'date-fns';
import type { Row } from 'write-excel-file';
import writeXlsxFile from 'write-excel-file';

import { useUserContext } from 'src/components/app/context/UserContext';
import { useDownloadManager } from 'src/components/downloadManager/DownloadManagerContext';

export enum WidgetExportFilterType {
  RANGE = 'Range',
  TIME_RANGE_COMPARISON = 'Time Range Comparison',
  ORG_TREE = 'OrgTree',
  DEMOGRAPHICS = 'Demographic/Ticket Filters',
  SEARCH = 'Search',
}

export interface WidgetExportOptions {
  widgetName: string;
  rows: Row[];
  comparisonRows?: Row[];
  /**
   * Query parameters used to filter the current results.
   */
  queryFilters: Record<WidgetExportFilterType, string | undefined>;
}

/**
 * Custom hook for exporting widget data to an Excel file.
 *
 * @param exportOptions - The options for the widget export.
 * @returns A callback function that, when executed, generates and adds the Excel file to the download manager.
 */
const useWidgetExport = (exportOptions?: WidgetExportOptions) => {
  const { currentSurvey } = useUserContext(); // Access the current survey from the user context.
  const { addFrontendExport } = useDownloadManager(); // Access the download manager to handle file exports.

  return useCallback(async () => {
    if (!exportOptions) {
      return;
    }
    // Generate a timestamp for the file name.
    const timestamp = formatISO9075(new Date());
    const surveyName = currentSurvey?.name ?? 'My Follow-Up';
    const fileName = `${surveyName}_${exportOptions.widgetName}_${timestamp}.xlsx`;

    // Create rows for the applied filters.
    const filterRows: Row[] = [
      [
        {
          value: 'Filters applied:',
        },
      ],
      // Loop through the query filters and create rows for each filter with a value.
      ...Object.entries(exportOptions.queryFilters)
        .filter(([_, value]) => Boolean(value)) // Only include filters with a value.
        .map(([key, value]) => [[{ value: `${key}:` }, { value }]])
        .flat(),
    ];

    // Create a header row for the widget name.
    const headerRow: Row = [
      {
        value: 'Widget:',
        fontWeight: 'bold',
      },
      {
        value: exportOptions.widgetName,
        fontWeight: 'bold',
      },
    ];

    // Create rows for the main data.
    const regularRows: Row[] = [
      [{ value: exportOptions.queryFilters[WidgetExportFilterType.RANGE] }],
      ...exportOptions.rows,
    ];

    // Create rows for comparison data, if provided.
    const comparisonRows: Row[] = exportOptions.comparisonRows
      ? [
          [
            {
              value:
                exportOptions.queryFilters[
                  WidgetExportFilterType.TIME_RANGE_COMPARISON
                ],
            },
          ],
          ...exportOptions.comparisonRows,
        ]
      : [];

    // Generate the Excel file data.
    const fileData = await writeXlsxFile(
      [
        [],
        headerRow,
        ...filterRows,
        [],
        [],
        ...regularRows,
        [],
        ...comparisonRows,
      ],
      {},
    );

    // Add the generated file to the download manager for the user to download.
    addFrontendExport({
      fileData: fileData,
      fileName,
      fileType:
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      category: 'WidgetExport',
    });
  }, [exportOptions, addFrontendExport, currentSurvey]);
};

export default useWidgetExport;
